import { useEffect, useState, useCallback } from 'react';
import {
  Loader2, AlertCircle, Bug, RefreshCw, Search, CheckCircle2, ChevronDown, Cpu,
} from 'lucide-react';
import {
  listCrashLogs, resolveCrashLog, type CrashLogRow,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';

type PlatformFilter = 'all' | 'pc' | 'android';

/**
 * Crash Logs — Cabal Next (2026-10-20): crash da release do PC e do Android, enviado pelo NextGuard (ticket op 5)
 * na abertura seguinte do jogo. Cada linha diz a plataforma (PC/ANDROID) e a versão; expande o relatório completo.
 */
export default function GuardCrashLogsPage() {
  const { notify } = useToast();
  const [items, setItems] = useState<CrashLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [onlyUnresolved, setOnlyUnresolved] = useState(false);
  const [platform, setPlatform] = useState<PlatformFilter>('all');
  const [expanded, setExpanded] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listCrashLogs(300, onlyUnresolved));
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro');
    } finally {
      setLoading(false);
    }
  }, [onlyUnresolved]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleResolve = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      await resolveCrashLog(id);
      notify('success', 'Crash marcado como resolvido');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  const q = filter.trim().toLowerCase();
  const filtered = items.filter((r) =>
    (platform === 'all' || r.platform === platform)
    && (!q
      || (r.accountId || '').toLowerCase().includes(q)
      || (r.hwid || '').toLowerCase().includes(q)
      || (r.content || '').toLowerCase().includes(q)
      || (r.version || '').toLowerCase().includes(q)
      || String(r.userNum ?? '').includes(q)));

  const unresolved = items.filter((i) => !i.resolved).length;
  const countPc = items.filter((i) => i.platform === 'pc').length;
  const countAndroid = items.filter((i) => i.platform === 'android').length;

  // primeira linha do raw = resumo do crash (ex: "[AV] READ RIP=...")
  const summaryLine = (raw?: string | null) => {
    if (!raw) return '—';
    const line = raw.split(/\r?\n/).find((l) => l.trim().length > 0) || '';
    return line.length > 160 ? line.slice(0, 160) + '…' : line;
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Bug className="text-red-400" size={20} /> Crash Logs
          </h2>
          <p className="text-sm text-white/65 mt-1">
            {items.length.toLocaleString('pt-BR')} crashes ·
            <span className="text-red-400 font-semibold ml-1">
              {unresolved.toLocaleString('pt-BR')} não resolvidos
            </span>
            <span className="text-white/40">
              {' '}· {countPc} PC · {countAndroid} Android · enviados pelo NextGuard na próxima abertura do jogo
            </span>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex rounded-lg overflow-hidden border border-white/[0.08]">
            {([['all', 'Todos'], ['pc', 'PC'], ['android', 'Android']] as [PlatformFilter, string][]).map(([id, label]) => (
              <button
                key={id}
                onClick={() => setPlatform(id)}
                className={`px-3 py-2 text-xs font-semibold transition cursor-pointer ${
                  platform === id ? 'bg-[#00e1d7]/15 text-[#00e1d7]' : 'bg-white/[0.04] text-white/65 hover:bg-white/[0.08]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setOnlyUnresolved((v) => !v)}
            className={`px-3 py-2 rounded-lg text-xs font-semibold transition cursor-pointer ${
              onlyUnresolved
                ? 'bg-red-500/15 border border-red-500/30 text-red-400'
                : 'bg-white/[0.04] border border-white/[0.08] text-white/65 hover:bg-white/[0.08]'
            }`}
          >
            {onlyUnresolved ? 'Só não resolvidos' : 'Todos'}
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                       bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7]
                       hover:bg-[#00e1d7]/20 transition cursor-pointer disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Atualizar
          </button>
        </div>
      </div>

      <div className="mb-4 relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtrar por conta, HWID, UserNum ou texto do crash…"
          className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg pl-11 pr-4 py-2.5
                     text-sm text-white placeholder:text-white/40
                     focus:outline-none focus:border-[#00e1d7]/40 transition"
        />
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <div className="surface-card rounded-xl overflow-hidden">
        {loading && items.length === 0 ? (
          <div className="py-16 flex justify-center text-white/40"><Loader2 size={20} className="animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-white/40 text-sm">
            {q ? 'Nenhum resultado' : 'Nenhum crash reportado 🎉'}
          </div>
        ) : (
          <div className="max-h-[72vh] overflow-y-auto">
            {filtered.map((log) => {
              const open = expanded === log.id;
              return (
                <div key={log.id} className="border-b border-white/[0.04] last:border-0">
                  <div
                    onClick={() => setExpanded(open ? null : log.id)}
                    className="px-4 py-3 flex items-center gap-3 hover:bg-white/[0.02] cursor-pointer transition"
                  >
                    <ChevronDown
                      size={15}
                      className={`text-white/40 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
                    />
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded shrink-0 ${
                      log.resolved
                        ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                        : 'bg-red-500/15 text-red-400 border border-red-500/30'
                    }`}>
                      {log.resolved ? 'RESOLVIDO' : 'NOVO'}
                    </span>
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded shrink-0 w-[58px] text-center ${
                      log.platform === 'android'
                        ? 'bg-lime-500/15 text-lime-300 border border-lime-500/30'
                        : log.platform === 'pc'
                          ? 'bg-sky-500/15 text-sky-300 border border-sky-500/30'
                          : 'bg-white/[0.06] text-white/50 border border-white/[0.1]'
                    }`}>
                      {log.platform === 'android' ? 'ANDROID' : log.platform === 'pc' ? 'PC' : '?'}
                    </span>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-white truncate">
                          {log.accountId || (log.userNum ? `#${log.userNum}` : 'sem conta')}
                        </span>
                        {log.version ? (
                          <span className="text-[10px] text-white/40 font-mono shrink-0">{log.version}</span>
                        ) : log.dllVer != null && !log.platform && (
                          <span className="text-[10px] text-white/40 font-mono shrink-0">DLL v{log.dllVer}</span>
                        )}
                      </div>
                      <div className="text-[11px] text-white/55 font-mono truncate">{summaryLine(log.content)}</div>
                    </div>

                    <div className="hidden md:flex items-center gap-1.5 text-white/40 shrink-0" title={log.hwid || ''}>
                      <Cpu size={12} />
                      <span className="text-[10px] font-mono">{log.hwid ? log.hwid.slice(0, 12) + '…' : '—'}</span>
                    </div>

                    <span className="text-[11px] text-white/45 font-mono whitespace-nowrap shrink-0 hidden sm:block">
                      {new Date(log.crashedAt || log.receivedAt || '').toLocaleString('pt-BR')}
                    </span>

                    {!log.resolved && (
                      <button
                        onClick={(e) => handleResolve(e, log.id)}
                        title="Marcar como resolvido"
                        className="shrink-0 p-1.5 rounded-md text-white/45 hover:text-emerald-400 hover:bg-emerald-500/10 transition cursor-pointer"
                      >
                        <CheckCircle2 size={15} />
                      </button>
                    )}
                  </div>

                  {open && (
                    <div className="px-4 pb-3">
                      <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-white/50 font-mono mb-2">
                        <span>HWID: <span className="text-white/75">{log.hwid || '—'}</span></span>
                        <span>IP: <span className="text-white/75">{log.ip || '—'}</span></span>
                        <span>UserNum: <span className="text-white/75">{log.userNum ?? '—'}</span></span>
                        <span>Recebido: <span className="text-white/75">{log.receivedAt ? new Date(log.receivedAt).toLocaleString('pt-BR') : '—'}</span></span>
                      </div>
                      <pre className="text-[11px] text-white/75 font-mono bg-[#0d141b] border border-white/[0.05]
                                      rounded p-3 overflow-x-auto max-h-[50vh] overflow-y-auto whitespace-pre-wrap break-words">
                        {log.content || '(sem conteúdo)'}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
